import './applianceReviews.scss';

import FsLightbox from 'fslightbox-react';
import {
    ProductReviews,
    ProductReviewsReview,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/models/productReviews';
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import Stars, {
    AdditionalInfoDisplayLocation,
    AdditionalInfoType,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Reviews/Stars';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { Input } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Pagination from '../Pagination';
import { ReviewsService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/interfaces/services/reviewsService';
import ReviewsSorter from './ApplianceReviewsSorter';
import arrowIconGray from '@progymedia/sbi-web-occ/src/images/icons/Icon-Fleche-Gray.svg';
import arrowIconVentis from '@progymedia/sbi-web-occ/src/images/icons/Icon-Fleche-VentisColor.svg';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { useCreateReviewAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/reviews';
import { useGetReviewRatingsMetadataQuery } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/reviews/queries';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { useOtherBoutikService } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useOtherBoutikService';
import { getBase64, removeWhiteSpaces } from '../../utils';

function SingleReview(props: {
    review: ProductReviewsReview;
    reviewImgUrls: Record<string, string>;
}) {
    const { review, reviewImgUrls } = props;
    const descriptionRef = useRef<HTMLSpanElement>(null);
    const [isLightBoxVisible, setIsLightBoxVisible] = useState(false);
    const currentDevice = useMobileDetect();

    const reviewsService: ReviewsService =
        useOtherBoutikService('ReviewsService');

    const reviewImage =
        reviewImgUrls[reviewsService.generateImageReviewId(review)];

    return (
        <div className='review'>
            <div className='review-head'>
                <span className='reviewer'>{review.nickname}</span>
                <span>{review.summary}</span>
                <span className='rating'>
                    <Stars
                        rating={review.rating}
                        additionalInfoType={AdditionalInfoType.STARS_ONLY}
                    />
                </span>
            </div>
            <span ref={descriptionRef} className='description'>
                {review.text}
                {reviewImage && (
                    <>
                        <div
                            className={`review-image ${
                                currentDevice.isMobile() ? 'mobile' : 'desktop'
                            }`}
                        >
                            <img
                                className='review-img'
                                src={reviewImage}
                                onClick={() =>
                                    setIsLightBoxVisible(!isLightBoxVisible)
                                }
                                alt=''
                            />
                        </div>
                        <FsLightbox
                            toggler={isLightBoxVisible}
                            sources={[reviewImage]}
                            exitFullscreenOnClose={true}
                        />
                    </>
                )}
            </span>
        </div>
    );
}

function AddReviewForm(props: any) {
    const { setCreatingReview, sku } = props;
    const [errors, setErrors] = useState({
        name: '',
        title: '',
        comment: '',
        starRating: '',
    });
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');
    const [newRating, setNewRating] = useState(0);
    const { t } = useTranslation('templatePages');
    const [createReview] = useCreateReviewAction();
    const [reviewAttachments, setReviewAttachments]: [
        string | undefined,
        React.Dispatch<React.SetStateAction<any>>
    ] = useState(undefined);
    const currentDevice = useMobileDetect();
    const { addAlert } = useContext(feedbackContext);

    const reviewsMetadata =
        useGetReviewRatingsMetadataQuery()[0]?.productReviewRatingsMetadata
            ?.items[0];

    const isFormValid = useCallback(() => {
        let isValid = true;

        for (const value of Object.values(errors)) {
            if (value.trim() !== '') {
                isValid = false;
                break;
            }
        }

        if (name === '') {
            setErrors((errors) => ({ ...errors, name: ' ' }));
            isValid = false;
        }

        if (title === '') {
            setErrors((errors) => ({ ...errors, title: ' ' }));
            isValid = false;
        }

        if (comment === '') {
            setErrors((errors) => ({ ...errors, comment: 'error' }));
            isValid = false;
        }

        if (newRating === 0) {
            setErrors((errors) => ({ ...errors, starRating: 'error' }));
            isValid = false;
        }
        return isValid;
    }, [comment, errors, name, newRating, title]);

    const onSubmit = useCallback(
        async (event: any) => {
            event.preventDefault();

            if (isFormValid()) {
                await createReview({
                    sku: sku,
                    nickname: name,
                    summary: title,
                    text: comment,
                    ratings: [
                        {
                            id: reviewsMetadata?.id,
                            value_id:
                                reviewsMetadata?.values[newRating - 1]
                                    ?.value_id,
                        },
                    ],
                })
                    .then(async (data) => {
                        if (reviewAttachments) {
                            const b64ObjType = (
                                reviewAttachments as string
                            ).split(`,`)[0];
                            let type = 'image/jpeg';
                            if (b64ObjType.includes('png')) {
                                type = 'image/png';
                            } else if (b64ObjType.includes('jpg')) {
                                type = 'image/jpg';
                            }
                            const reviewObj =
                                data?.data?.createProductReview?.review;

                            const reviewId = generateReviewId(
                                reviewObj?.nickname as string,
                                reviewObj?.created_at as string,
                                sku ?? ''
                            );
                            const requestOptions = {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    reviewId: reviewId,
                                    sku: sku,
                                    nickname: reviewObj?.nickname,
                                    site: 'ccanada.com',
                                    created_at: reviewObj?.created_at,
                                    filedata: reviewAttachments,
                                    type: type,
                                }),
                            };
                            await fetch(`/inquiry/reviews`, requestOptions);
                        }
                        setCreatingReview(false);
                        addAlert({
                            message: t('toastMessages:review-message'),
                            status: 'success',
                        });
                    })
                    .catch((error: any) => {
                        addAlert({ message: error, status: 'error' });
                        console.error(error.message);
                    });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            addAlert,
            comment,
            createReview,
            isFormValid,
            name,
            newRating,
            reviewsMetadata?.id,
            reviewsMetadata?.values,
            setCreatingReview,
            sku,
            t,
            title,
        ]
    );

    const uploadPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentFiles = event?.target?.files;
        if (currentFiles && currentFiles?.length > 0) {
            const base64 = await getBase64(currentFiles[0]);
            setReviewAttachments(base64);
        } else return undefined;
    };

    const attachPhoto = () => {
        const fileInput = document.getElementById('review-fileinput');
        if (fileInput) {
            fileInput.click();
        } else return undefined;
    };

    return (
        <>
            <form
                autoComplete='off'
                onSubmit={(event) => onSubmit(event)}
                className='review-form'
            >
                <div className='review-form-box'>
                    <div className='inputGroup'>
                        <h3>{t('review.name')}</h3>
                        <Input
                            className='review-input'
                            type='text'
                            error={errors.name}
                            value={name}
                            onChange={(value: string) => {
                                setErrors((errors) => ({
                                    ...errors,
                                    name: '',
                                }));
                                setName(value);
                            }}
                            placeholder={t('review.name-placeholder')}
                        />

                        <h3>{t('review.title')}</h3>
                        <Input
                            type='text'
                            className='review-input'
                            error={errors.title}
                            value={title}
                            onChange={(value: string) => {
                                setErrors((errors) => ({
                                    ...errors,
                                    title: '',
                                }));
                                setTitle(value);
                            }}
                            placeholder={t('review.title-placeholder')}
                        />
                        <h3>{t('review.rate')}</h3>
                        <div
                            className={`review-starRating first ${
                                errors.starRating === 'error'
                                    ? ' error'
                                    : 'review-starRating'
                            }`}
                        >
                            <span
                                className={
                                    newRating === 5
                                        ? 'review-star selected'
                                        : 'review-star '
                                }
                                onClick={() => {
                                    setNewRating(5);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                            <span
                                className={
                                    newRating === 4
                                        ? 'review-star  selected'
                                        : 'review-star '
                                }
                                onClick={() => {
                                    setNewRating(4);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                            <span
                                className={
                                    newRating === 3
                                        ? 'review-star  selected'
                                        : 'review-star '
                                }
                                onClick={() => {
                                    setNewRating(3);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                            <span
                                className={
                                    newRating === 2
                                        ? 'review-star  selected'
                                        : 'review-star '
                                }
                                onClick={() => {
                                    setNewRating(2);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                            <span
                                className={`review-star ${
                                    newRating === 1
                                        ? '  selected'
                                        : 'review-star '
                                } first`}
                                onClick={() => {
                                    setNewRating(1);
                                    setErrors((errors) => ({
                                        ...errors,
                                        starRating: '',
                                    }));
                                }}
                            />
                        </div>
                    </div>

                    <div className='inputGroup'>
                        <h3>{t('review.comment')}</h3>
                        <textarea
                            className={
                                errors.comment ? 'comment error' : 'comment'
                            }
                            onChange={(event) => {
                                setComment(event.currentTarget.value);
                                setErrors((errors) => ({
                                    ...errors,
                                    comment: '',
                                }));
                            }}
                            placeholder={t('review.comment-placeholder')}
                        />
                    </div>
                    <input
                        style={{ display: 'none' }}
                        type='file'
                        accept='.jpg, .jpeg, .png'
                        onChange={(e) => uploadPhoto(e)}
                        id='review-fileinput'
                    />
                    <div className='btn-wrapper'>
                        <Button
                            className='send-review'
                            label={t('review.send')}
                        />
                    </div>
                </div>
                <div className='add-picture-box'>
                    <div onClick={attachPhoto} className='add-picture'>
                        <span>{t('review.add-image')}</span>
                    </div>
                    {reviewAttachments && (
                        <div
                            className={`attachments-div ${
                                currentDevice.isMobile() ? 'mobile' : 'desktop'
                            }`}
                        >
                            <img
                                src={reviewAttachments}
                                alt=''
                                className='review-img-preview'
                            />

                            <span
                                onClick={() => {
                                    setReviewAttachments(null);
                                }}
                                className='delete-img'
                            >
                                X
                            </span>
                        </div>
                    )}
                </div>
            </form>
        </>
    );
}

const ApplianceReviews = (props: { code: string }) => {
    const { code } = props;
    const reviewsService: ReviewsService =
        useOtherBoutikService('ReviewsService');
    const [productReviews, setProductReviews] = useState<ProductReviews>();
    const [sortedReviews, setSortedReviews] = useState<ProductReviewsReview[]>(
        []
    );
    const { t } = useTranslation('templatePages');
    const [isCreatingReview, setCreatingReview] = useState(false);
    const [isHover, setHover] = useState(false);
    const [page, setPage] = useState(1);
    const currentDevice = useMobileDetect();
    const reviewsPerPage = 5;

    const [reviewImgUrls, setReviewImgUrls] = useState<Record<string, string>>(
        {}
    );

    useEffect(() => {
        reviewsService
            .getReviewsBySku(code)
            .then((reviewsObject: ProductReviews | null) => {
                if (reviewsObject?.reviews) {
                    setProductReviews(reviewsObject);
                    setSortedReviews(reviewsObject?.reviews);
                }
            })
            .catch((e) => console.error(e));

        reviewsService
            .getImageReviewUrls(code)
            .then((urls) => {
                setReviewImgUrls(urls);
            })
            .catch((e) => console.log(e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const hasReviews = (): boolean => {
        return sortedReviews && sortedReviews?.length > 0;
    };

    return (
        <div className='appliance-reviews'>
            <div className='appliance-reviews-header'>
                <div className='row'>
                    <span className='title'>{t('review.reviews')}</span>

                    {hasReviews() ? (
                        currentDevice.isMobile() && (
                            <div>
                                <span className='rating'>
                                    <Stars
                                        rating={
                                            productReviews?.average as number
                                        }
                                        additionalInfoDisplayLocation={
                                            AdditionalInfoDisplayLocation.LEFT
                                        }
                                        additionalInfoType={
                                            AdditionalInfoType.AVERAGE
                                        }
                                    />
                                </span>
                            </div>
                        )
                    ) : (
                        <Button
                            className='add-review-btn'
                            label={t('review.add-review')}
                            onClick={() => setCreatingReview(!isCreatingReview)}
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            iconRight={
                                isHover ? arrowIconVentis : arrowIconGray
                            }
                        />
                    )}
                </div>

                {hasReviews() && (
                    <>
                        {currentDevice.isMobile() && (
                            <div className='row'>
                                <Button
                                    className='add-review-btn'
                                    label={t('review.add-review')}
                                    onClick={() =>
                                        setCreatingReview(!isCreatingReview)
                                    }
                                    onMouseEnter={() => setHover(true)}
                                    onMouseLeave={() => setHover(false)}
                                    iconRight={
                                        isHover
                                            ? arrowIconVentis
                                            : arrowIconGray
                                    }
                                />
                            </div>
                        )}
                        <div className='row sort-reviews'>
                            <div>
                                {!currentDevice.isMobile() && (
                                    <span className='rating'>
                                        <Stars
                                            rating={
                                                productReviews?.average as number
                                            }
                                            additionalInfoDisplayLocation={
                                                AdditionalInfoDisplayLocation.LEFT
                                            }
                                            additionalInfoType={
                                                AdditionalInfoType.AVERAGE
                                            }
                                        />
                                    </span>
                                )}
                                <ReviewsSorter
                                    reviews={sortedReviews}
                                    setReviews={setSortedReviews}
                                />
                            </div>

                            {!currentDevice.isMobile() && (
                                <Button
                                    className='add-review-btn'
                                    label={t('review.add-review')}
                                    onClick={() =>
                                        setCreatingReview(!isCreatingReview)
                                    }
                                    onMouseEnter={() => setHover(true)}
                                    onMouseLeave={() => setHover(false)}
                                    iconRight={
                                        isHover
                                            ? arrowIconVentis
                                            : arrowIconGray
                                    }
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            {isCreatingReview && (
                <AddReviewForm
                    setCreatingReview={setCreatingReview}
                    sku={code}
                />
            )}

            <div className='reviews'>
                {hasReviews() ? (
                    sortedReviews
                        ?.slice(
                            reviewsPerPage * (page - 1),
                            reviewsPerPage * page
                        )
                        ?.map((review: any, index: number) => {
                            return (
                                <SingleReview
                                    key={index}
                                    review={review}
                                    reviewImgUrls={reviewImgUrls}
                                />
                            );
                        })
                ) : (
                    <p className='no-review'>{t('review.no-reviews')}</p>
                )}
            </div>

            {sortedReviews && sortedReviews.length / reviewsPerPage > 1 && (
                <Pagination
                    currentPage={page}
                    onPageChange={(newPage: number) => setPage(newPage)}
                    pageNumber={Math.ceil(
                        sortedReviews.length / reviewsPerPage
                    )}
                />
            )}
        </div>
    );
};

function generateReviewId(
    reviewer: string,
    dateCreated: string,
    sku: string
): string {
    const formatedDate = moment(dateCreated).format('YYYYMMDD');
    return removeWhiteSpaces(`${sku}-${reviewer}-${formatedDate}`);
}

export default ApplianceReviews;
